import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { walletAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Wallet, Plus, ArrowLeft, CheckCircle, CreditCard, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function WalletTopupPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const method = searchParams.get('method');

  const checkStripeStatus = useCallback(async (sid) => {
    setCheckingPayment(true);
    try {
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 2000;

      const poll = async () => {
        const response = await walletAPI.topupStatus(sid);
        
        if (response.data.payment_status === 'paid') {
          setPaymentSuccess(true);
          await refreshUser();
          toast.success('Wallet topped up successfully!');
          return;
        }
        
        if (response.data.status === 'expired' || attempts >= maxAttempts) {
          toast.error('Payment session expired or failed');
          setCheckingPayment(false);
          return;
        }
        
        attempts++;
        setTimeout(poll, pollInterval);
      };

      await poll();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCheckingPayment(false);
    }
  }, [refreshUser]);

  const capturePaypalPayment = useCallback(async (oid) => {
    setCheckingPayment(true);
    try {
      const response = await walletAPI.capturePaypal(oid);
      if (response.data.status === 'success' || response.data.status === 'already_captured') {
        setPaymentSuccess(true);
        await refreshUser();
        toast.success('Wallet topped up successfully!');
      } else {
        toast.error('PayPal payment failed');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCheckingPayment(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    if (sessionId && method === 'stripe') {
      checkStripeStatus(sessionId);
    } else if (orderId && method === 'paypal') {
      capturePaypalPayment(orderId);
    }
  }, [sessionId, orderId, method, checkStripeStatus, capturePaypalPayment]);

  const handleTopup = async (e) => {
    e.preventDefault();
    
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount < 1) {
      toast.error('Minimum amount is $1.00');
      return;
    }
    
    setLoading(true);
    try {
      const response = await walletAPI.topup(parsedAmount, window.location.origin, paymentMethod);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(formatApiError(error));
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const quickAmounts = [10, 25, 50, 100];

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col">
        <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
            </Link>
          </div>
        </header>
        
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none text-center">
            <CardContent className="p-8">
              <div className="animate-spin w-12 h-12 border-4 border-[#22C55E] border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Processing Payment
              </h2>
              <p className="text-[#999999]">
                Please wait while we confirm your payment...
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col">
        <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
            </Link>
          </div>
        </header>
        
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none text-center" data-testid="topup-success-card">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#22C55E]" weight="fill" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Money Added Successfully!
              </h2>
              <p className="text-[#999999] mb-2">
                Your wallet has been topped up.
              </p>
              <p className="text-3xl font-bold text-[#22C55E] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
                {formatCurrency(user?.wallet_balance || 0)}
              </p>
              <Link to="/dashboard">
                <Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="back-to-dashboard-button">
                  Back to Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="wallet-topup-card">
          <CardHeader className="p-8 pb-6">
            <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
            <CardTitle className="text-2xl font-bold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <div className="w-10 h-10 bg-[#22C55E]/10 rounded-sm flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#22C55E]" weight="duotone" />
              </div>
              Add Money
            </CardTitle>
            <CardDescription className="text-[#999999]">
              Add funds using Card, Cash App, or PayPal
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            {/* Current Balance */}
            <div className="bg-[#111111] rounded-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999] mb-1">
                    Current Balance
                  </p>
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="current-balance">
                    {formatCurrency(user?.wallet_balance || 0)}
                  </p>
                </div>
                <Link to="/wallet/withdraw" data-testid="withdraw-link">
                  <Button variant="outline" className="border-[#333333] text-[#22C55E] hover:bg-[#22C55E]/10 font-semibold">
                    Transfer to Bank
                  </Button>
                </Link>
              </div>
            </div>

            <form onSubmit={handleTopup} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">
                  Amount to Add
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999] font-medium">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="rounded-sm bg-[#111111] border-[#333333] text-white placeholder:text-[#666666] pl-8 text-xl font-bold focus:ring-2 focus:ring-[#22C55E] focus:ring-offset-1 focus:ring-offset-[#1A1A1A]"
                    data-testid="topup-amount-input"
                  />
                </div>
              </div>

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-4 gap-2">
                {quickAmounts.map((amt) => (
                  <Button
                    key={amt}
                    type="button"
                    variant="outline"
                    onClick={() => setAmount(amt.toString())}
                    className={`rounded-sm border-[#333333] ${
                      parseFloat(amount) === amt 
                        ? 'bg-[#22C55E] text-white border-[#22C55E] hover:bg-[#22C55E]' 
                        : 'bg-transparent text-white hover:bg-[#333333]'
                    }`}
                    data-testid={`quick-amount-${amt}`}
                  >
                    ${amt}
                  </Button>
                ))}
              </div>

              {/* Payment Method Selection */}
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">
                  Payment Method
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPaymentMethod('stripe')}
                    className={`rounded-sm h-16 flex flex-col items-center justify-center gap-1 ${
                      paymentMethod === 'stripe'
                        ? 'bg-[#22C55E]/10 border-[#22C55E] text-white'
                        : 'bg-transparent border-[#333333] text-white hover:bg-[#333333]'
                    }`}
                    data-testid="stripe-method-button"
                  >
                    <CreditCard className="h-5 w-5 text-[#6772E5]" weight="duotone" />
                    <span className="text-xs">Card</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPaymentMethod('cashapp')}
                    className={`rounded-sm h-16 flex flex-col items-center justify-center gap-1 ${
                      paymentMethod === 'cashapp'
                        ? 'bg-[#22C55E]/10 border-[#22C55E] text-white'
                        : 'bg-transparent border-[#333333] text-white hover:bg-[#333333]'
                    }`}
                    data-testid="cashapp-method-button"
                  >
                    <Lightning className="h-5 w-5 text-[#00D632]" weight="fill" />
                    <span className="text-xs">Cash App</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`rounded-sm h-16 flex flex-col items-center justify-center gap-1 ${
                      paymentMethod === 'paypal'
                        ? 'bg-[#22C55E]/10 border-[#22C55E] text-white'
                        : 'bg-transparent border-[#333333] text-white hover:bg-[#333333]'
                    }`}
                    data-testid="paypal-method-button"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.566c2.168 0 3.72.486 4.588 1.397.823.862 1.124 2.114.894 3.72l-.014.08v.76l.59.333c.502.264.897.584 1.187.963.322.419.523.925.598 1.5.077.594.024 1.286-.158 2.063-.21.895-.548 1.673-1.006 2.309a4.833 4.833 0 0 1-1.54 1.445c-.59.358-1.273.61-2.027.749-.735.135-1.555.205-2.438.205h-.58a1.73 1.73 0 0 0-1.711 1.462l-.043.222-.726 4.596-.033.162a.206.206 0 0 1-.203.173H7.076z" fill="#253B80"/>
                      <path d="M19.948 8.33a8.37 8.37 0 0 0-.093-.387c-1.082-3.566-4.39-4.793-8.028-4.793H5.657a.956.956 0 0 0-.945.81L2.471 20.575a.575.575 0 0 0 .568.663h4.134l1.038-6.581-.032.207a.953.953 0 0 1 .942-.806h1.96c3.853 0 6.869-1.564 7.751-6.09.026-.133.048-.264.068-.393.26-1.66-.002-2.788-.952-3.808a3.67 3.67 0 0 0-.957-.437z" fill="#179BD7"/>
                    </svg>
                    <span className="text-xs">PayPal</span>
                  </Button>
                </div>
                <p className="text-[10px] text-[#666666] mt-1">
                  {paymentMethod === 'stripe' && 'Supports Apple Pay & Google Pay on compatible devices'}
                  {paymentMethod === 'cashapp' && 'Pay with your Cash App balance or linked bank'}
                  {paymentMethod === 'paypal' && 'Also supports Venmo through PayPal checkout'}
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || !amount || parseFloat(amount) < 1}
                className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold"
                data-testid="topup-submit-button"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : `Add $${parseFloat(amount || 0).toFixed(2)} with ${paymentMethod === 'stripe' ? 'Card' : paymentMethod === 'cashapp' ? 'Cash App' : 'PayPal'}`}
              </Button>
            </form>

            <div className="mt-6 flex flex-col items-center gap-2">
              <div className="flex items-center justify-center gap-3 text-xs text-[#666666]" data-testid="payment-providers">
                <div className="flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5 text-[#6772E5]" weight="duotone" />
                  <span>Card</span>
                </div>
                <span className="text-[#333333]">|</span>
                <div className="flex items-center gap-1">
                  <span className="text-[#555555] text-[10px] font-medium">Apple Pay</span>
                </div>
                <span className="text-[#333333]">|</span>
                <div className="flex items-center gap-1">
                  <Lightning className="w-3.5 h-3.5 text-[#00D632]" weight="fill" />
                  <span>Cash App</span>
                </div>
                <span className="text-[#333333]">|</span>
                <div className="flex items-center gap-1">
                  <span className="text-[#0070BA] font-bold text-[10px]">P</span>
                  <span>PayPal / Venmo</span>
                </div>
              </div>
              <span className="text-[10px] text-[#444444]">Secure payments powered by Stripe & PayPal</span>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
